import { Combobox } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'
import { Fragment, useMemo, useState } from 'react'
import { cn } from '../lib/utils'

/**
 * BankCombobox — selector de banco con filtro al escribir.
 * Trabaja con string plano (el nombre del banco). Si escribís uno que no está
 * en la lista, ofrece usarlo como texto libre.
 *
 *   <BankCombobox value={banco} onChange={setBanco} className="h-8 text-xs" />
 */

export const BANCOS = [
  'Banco Nación', 'Banco Provincia', 'Banco Santander', 'Banco Galicia', 'Banco Macro', 'Banco BBVA',
  'Banco HSBC', 'Banco Itaú', 'Banco Supervielle', 'Banco Patagonia', 'Banco Comafi', 'Banco Industrial',
  'Banco Ciudad', 'Banco Credicoop', 'Banco Hipotecario', 'ICBC', 'Brubank', 'Banco Columbia',
  'Banco Corrientes', 'Banco Chaco', 'Banco Formosa', 'Banco Jujuy', 'Banco La Pampa', 'Banco La Rioja',
  'Banco Mendoza', 'Banco Neuquén', 'Banco Río Negro', 'Banco Salta', 'Banco San Juan', 'Banco San Luis',
  'Banco Santa Cruz', 'Banco Santa Fe', 'Banco Santiago del Estero', 'Banco Tierra del Fuego', 'Banco Tucumán',
  'Bancor (Banco de Córdoba)', 'Banco Bica', 'Banco CMF', 'Banco de Comercio', 'Banco del Sol', 'Banco Dino',
  'Banco Julio', 'Banco Mariva', 'Banco Masventas', 'Banco Meridian', 'Banco Piano', 'Banco Roela',
  'Banco Sáenz', 'Banco Voii', 'BST Banco de Servicios y Transacciones',
]

const norm = (s: string) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')

export default function BankCombobox({
  value,
  onChange,
  placeholder = 'Buscar banco...',
  className,
  disabled,
}: {
  value?: string
  onChange: (bank: string) => void
  placeholder?: string
  className?: string
  disabled?: boolean
}) {
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const q = norm(query.trim())
    if (!q) return BANCOS
    return BANCOS.filter((b) => norm(b).includes(q))
  }, [query])

  const trimmed = query.trim()
  const showFree = trimmed.length > 0 && !BANCOS.some((b) => norm(b) === norm(trimmed))

  return (
    <Combobox value={value || ''} onChange={(v: string) => onChange(v || '')} disabled={disabled}>
      <div className="relative">
        <div className="relative">
          <Combobox.Input
            className={cn(
              'w-full h-9 px-3 pr-8 border border-input rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed',
              className
            )}
            placeholder={placeholder}
            displayValue={(v: string) => v || ''}
            onChange={(e) => setQuery(e.target.value)}
          />
          <Combobox.Button className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
            <ChevronUpDownIcon className="h-4 w-4" aria-hidden="true" />
          </Combobox.Button>
        </div>

        <Combobox.Options className="absolute z-50 mt-1 max-h-56 w-full min-w-[230px] overflow-auto rounded-lg shadow-lg border bg-card">
          {filtered.map((b) => (
            <Combobox.Option key={b} value={b} as={Fragment}>
              {({ active, selected }) => (
                <li className={cn('relative cursor-pointer select-none px-3 py-1.5 text-sm flex items-center justify-between gap-2', active ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50')}>
                  <span className={selected ? 'font-semibold' : 'font-normal'}>{b}</span>
                  {selected && <CheckIcon className="h-4 w-4 text-green-600 shrink-0" aria-hidden="true" />}
                </li>
              )}
            </Combobox.Option>
          ))}
          {showFree && (
            <Combobox.Option value={trimmed} as={Fragment}>
              {({ active }) => (
                <li className={cn('relative cursor-pointer select-none px-3 py-1.5 text-sm border-t', active ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50')}>
                  Usar «{trimmed}»
                </li>
              )}
            </Combobox.Option>
          )}
        </Combobox.Options>
      </div>
    </Combobox>
  )
}
